import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { Router } from '@angular/router';
import { ColumnConfig, ActionConfig } from './tabular-data.interfaces';
import { JOB_STATUS_ID } from '@dal/core';
import { AssetTypeId } from '../../enums/asset-type.enum';

@Component({
  selector: 'dal-shared-ui-tabular-data',
  templateUrl: './tabular-data.component.html',
  styleUrl: './tabular-data.component.scss',
})
export class TabularDataComponent {
  @Input() dataSource: Record<string, unknown>[] = [];
  @Input() title: string;
  @Input() displayedColumns: ColumnConfig[] = [];
  @Input() dialogLink = false;
  @Input() enableSorting = false;
  @Input() customActions: ActionConfig[] = [];
  sortOrder: string = '';
  sortColumn: string = '';

  // Make enums available to template
  readonly AssetTypeId = AssetTypeId;

  @Output() dialogOpened = new EventEmitter<Record<string, unknown>>();
  @Output() downloadReport = new EventEmitter<number[]>();
  @Output() actionClicked = new EventEmitter<{
    action: string;
    element: Record<string, unknown>;
  }>();
  @Output() sortChanged = new EventEmitter<{
    column: string;
    order: string;
  }>();

  constructor(
    private router: Router,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer
  ) {
    iconRegistry.addSvgIcon(
      'arrow-repeat',
      sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/arrow-repeat.svg')
    );
    iconRegistry.addSvgIcon(
      'sort-alt',
      sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/sort-alt.svg')
    );
    iconRegistry.addSvgIcon(
      'actions',
      sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/actions.svg')
    );
    iconRegistry.addSvgIcon(
      'exclamation-square-fill',
      sanitizer.bypassSecurityTrustResourceUrl(
        '/assets/icons/exclamation-square-fill.svg'
      )
    );
  }

  toggleSort(order: string, column: string) {
    console.log('toggleSort called:', order, column); // Debug log
    if (this.sortColumn === column && this.sortOrder === order) {
      // If the column is already sorted in this order, reset
      this.sortOrder = '';
      this.sortColumn = '';
      this.sortChanged.emit({ column: '', order: '' });
    } else {
      this.sortOrder = order;
      this.sortColumn = column;
      this.sortChanged.emit({ column, order });
    }
  }

  triggerDownload(element: Record<string, unknown>) {
    const tasks = element['tasks'] as { asset_id: number }[];
    this.downloadReport.emit(tasks.map((task) => task.asset_id));
  }

  navigate(element: Record<string, unknown>) {
    this.router.navigateByUrl(`/search?jobID=${element['job_id']}`);
  }

  openDialog(element: Record<string, unknown>) {
    this.dialogOpened.emit(element);
  }

  // Helper methods for column configuration
  get displayedColumnKeys(): string[] {
    return this.displayedColumns.map((col) => col.key);
  }

  getFilteredActions(element: Record<string, unknown>): ActionConfig[] {
    return this.customActions.filter(
      (action) => !action.condition || action.condition(element)
    );
  }

  onActionClick(action: string, element: Record<string, unknown>) {
    this.actionClicked.emit({ action, element });
  }

  isSuccessfulJob(element: Record<string, unknown>): boolean {
    return element['status_id'] === JOB_STATUS_ID.SUCCESS;
  }

  navigateIfSuccessful(element: Record<string, unknown>): void {
    if (this.isSuccessfulJob(element)) {
      this.navigate(element);
    }
  }
}
